import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import API_URL from '../config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from '../utils/axiosConfig'; // CSRF設定
import { motion, AnimatePresence } from 'framer-motion'; // 導入 framer-motion
import { FaBuilding, FaUser, FaPhone, FaEnvelope, FaLightbulb, FaHandsHelping, FaInfoCircle, FaCommentDots } from 'react-icons/fa'; // 導入 react-icons


function BecomePartner() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    desiredFeatures: '',
    assistance: '',
    otherContactDetails: '',
    additionalMessage: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(false); // 電子郵件的錯誤狀態

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (field === 'email' && value) {
      setEmailError(false); // 清除電子郵件錯誤
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // 驗證電子郵件是否填寫
    if (!formData.email) {
      setEmailError(true); // 顯示電子郵件錯誤
      toast.error(t('emailRequired'));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/partner`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 201) {
        toast.success(t('formSubmittedSuccessfully'));

        // 重置表單
        setFormData({
          companyName: '',
          contactName: '',
          phone: '',
          email: '',
          desiredFeatures: '',
          assistance: '',
          otherContactDetails: '',
          additionalMessage: '',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('formSubmissionFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const buttonVariants = {
    whileHover: { scale: 1.05 },
    whileTap: { scale: 0.95 },
  };

  return (
    <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900 dark:to-gray-800 min-h-screen text-gray-800 dark:text-gray-100 font-sans">
      <div className="container mx-auto px-6 py-20 flex flex-col items-center justify-center">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold text-center mb-8 tracking-tight leading-tight text-gray-800 dark:text-gray-100"
        >
          {t('becomePartner')}
        </motion.h1>

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          onSubmit={handleSubmit}
          className="w-full max-w-md space-y-6" // 增加間距
        >
        {/* 公司名稱 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
            <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaBuilding className="mr-2" /> {/* 公司圖示 */}
              {t('companyName')}
            </label>
            <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleChange('companyName', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('companyNamePlaceholder')}
          />
        </motion.div>

        {/* 聯絡人姓名 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
          <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaUser className="mr-2" /> {/* 聯絡人圖示 */}
              {t('contactName')}
            </label>
            <input
            type="text"
            value={formData.contactName}
            onChange={(e) => handleChange('contactName', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('contactNamePlaceholder')}
          />
        </motion.div>

        {/* 電話 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
          <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaPhone className="mr-2" /> {/* 電話圖示 */}
              {t('phone')}
            </label>
            <input
            type="text"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('phonePlaceholder')}
          />
        </motion.div>

        {/* 電子郵件 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
          <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaEnvelope className="mr-2" /> {/* 電子郵件圖示 */}
              {t('email')}<span className="text-red-500">*</span>
            </label>
            <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={`w-full p-2 border rounded ${
              emailError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            } bg-white dark:bg-gray-800 dark:text-gray-100`}
            placeholder={t('emailPlaceholder')}
          />
          {emailError && (
            <p className="text-red-500 text-sm mt-1">{t('emailRequired')}</p>
          )}
        </motion.div>

        {/* 許願未來功能 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
            <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaLightbulb className="mr-2" /> {/* 功能圖示 */}
              {t('desiredFeatures')}
            </label>
            <textarea
            value={formData.desiredFeatures}
            onChange={(e) => handleChange('desiredFeatures', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('desiredFeaturesPlaceholder')}
          />
        </motion.div>

        {/* 可以提供的協助 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
          <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaHandsHelping className="mr-2" /> {/* 協助圖示 */}
              {t('assistance')}
            </label>
            <textarea
            value={formData.assistance}
            onChange={(e) => handleChange('assistance', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('assistancePlaceholder')}
          />
        </motion.div>

        {/* 其他聯絡資訊 */}
          {/* 其他聯絡資訊 */}
          <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
          <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaInfoCircle className="mr-2" /> {/* 其他資訊圖示 */}
              {t('otherContactDetails')}
            </label>
            <textarea
            value={formData.otherContactDetails}
            onChange={(e) => handleChange('otherContactDetails', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('otherContactDetailsPlaceholder')}
          />
        </motion.div>

        {/* 想說的話 */}
        <motion.div variants={inputVariants} initial="hidden" animate="visible" className="flex flex-col">
            <label className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 flex items-center">
              <FaCommentDots className="mr-2" /> {/* 訊息圖示 */}
              {t('additionalMessage')}
            </label>
            <textarea
            value={formData.additionalMessage}
            onChange={(e) => handleChange('additionalMessage', e.target.value)}
            className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder={t('additionalMessagePlaceholder')}
          />
        </motion.div>

        {/* 提交按鈕 */}
        <motion.button
            variants={buttonVariants}
            whileHover="whileHover"
            whileTap="whileTap"
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 transition duration-300'
            }`}
          >
            {isSubmitting ? t('submitting') : t('submit')}
          </motion.button>
        </motion.form>
      </div>
    </div>
  );
}

export default BecomePartner;
